/**
 * Утилита для работы с изображениями
 */

/**
 * Сжимает изображение с заданными параметрами
 * @param {File|Blob|string} imageSource - Изображение в формате File, Blob или dataURL
 * @param {Object} options - Параметры сжатия
 * @returns {Promise<Blob>} - Сжатое изображение в формате Blob
 */
export async function compressImage(imageSource, options = {}) {
  // Параметры по умолчанию
  const settings = {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.8,
    ...options
  };

  return new Promise(async (resolve, reject) => {
    // Создаем объект изображения
    const img = new Image();
    let objectUrl;
    
    // Обработка ошибок
    img.onerror = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      reject(new Error('Не удалось загрузить изображение'));
    };
    
    // Когда изображение загружено
    img.onload = () => {
      // Освобождаем объект URL, если он был создан
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      
      // Создаем canvas для сжатия
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Вычисляем новые размеры с сохранением пропорций
      let width = img.width;
      let height = img.height;
      
      if (width > settings.maxWidth) {
        height = (settings.maxWidth / width) * height;
        width = settings.maxWidth;
      }
      
      if (height > settings.maxHeight) {
        width = (settings.maxHeight / height) * width;
        height = settings.maxHeight;
      }
      
      // Устанавливаем размеры canvas
      canvas.width = width;
      canvas.height = height;
      
      // Рисуем изображение на canvas с новыми размерами
      ctx.drawImage(img, 0, 0, width, height);
      
      // Конвертируем canvas в Blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Не удалось создать Blob из canvas'));
          }
        },
        'image/jpeg',
        settings.quality
      );
    };
    
    try {
      // Определяем тип источника и загружаем изображение
      if (typeof imageSource === 'string' && imageSource.startsWith('data:')) {
        // Если источник - Data URL
        img.src = imageSource;
      } else if (imageSource instanceof Blob || imageSource instanceof File) {
        // Если источник - Blob или File, создаем URL
        objectUrl = URL.createObjectURL(imageSource);
        img.src = objectUrl;
      } else {
        reject(new Error('Неподдерживаемый тип изображения'));
      }
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Конвертирует dataURL строку в File объект
 * @param {string} dataURL - dataURL строка
 * @param {string} filename - имя файла
 * @returns {File} - File объект
 */
export function dataURLtoFile(dataURL, filename) {
  // Извлекаем тип MIME и данные
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  // Заполняем массив байтами
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  // Создаем File объект
  return new File([u8arr], filename, { type: mime });
}

export default {
  compressImage,
  dataURLtoFile
};
